import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Heart, MessageCircle, ChevronRight, Pin } from 'lucide-react';
import { discussionService, type Discussion } from '@services/discussionService';
import { useNavigate } from 'react-router-dom';
import { formatChartDateDaily, formatRelativeTime } from '@utils/dateUtils';

const DiscussionCard = ({ discussion }: { discussion: Discussion }) => {
  const navigate = useNavigate();

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bug': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'feature': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'question': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'feedback': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'astrology': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatDate = (dateString: string) => {
    const relative = formatRelativeTime(dateString);
    if (relative.endsWith('ago') || relative === 'just now') return relative;
    return formatChartDateDaily(dateString);
  };

  return (
    <div
      onClick={() => navigate('/discussion')}
      className="flex-shrink-0 w-80 sm:w-96 bg-white/[0.08] border border-white/20 rounded-2xl p-5 hover:bg-white/[0.12] transition-all cursor-pointer group"
    >
      <div className="flex items-start gap-3 mb-3">
        {discussion.user_profile_picture ? (
          <img
            src={discussion.user_profile_picture}
            alt={discussion.user_name}
            className="w-10 h-10 rounded-full object-cover border border-white/20"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">
              {discussion.user_name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white font-semibold text-sm truncate">{discussion.user_name}</p>
            {discussion.pinned && (
              <Pin size={14} className="text-yellow-400 flex-shrink-0" />
            )}
            {discussion.admin_hearted && (
              <Heart size={14} className="text-pink-500 fill-pink-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-purple-300/60 text-xs">{formatDate(discussion.created_at)}</p>
        </div>
        <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide border flex-shrink-0 ${getTypeColor(discussion.type)}`}>
          {discussion.type}
        </span>
      </div>

      <p className="text-purple-100 text-sm leading-relaxed line-clamp-3 mb-4">
        {discussion.content}
      </p>

      <div className="flex items-center justify-between pt-3 border-t border-white/10">
        <div className="flex items-center gap-4 text-xs text-purple-300">
          <div className="flex items-center gap-1.5">
            <Heart size={14} />
            <span>{discussion.upvote_count}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MessageCircle size={14} />
            <span>{discussion.comment_count}</span>
          </div>
        </div>
        <ChevronRight size={16} className="text-purple-400 group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  );
};

const DiscussionsSection = () => {
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const topRowRef = useRef<HTMLDivElement>(null);
  const bottomRowRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDiscussions = async () => {
      try {
        setLoading(true);
        // Fetch more discussions to fill both rows
        const response = await discussionService.getDiscussions(1, 20);
        setDiscussions(response.discussions);
      } catch (error) {
        console.error('Error fetching discussions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscussions();
  }, []);

  // Split discussions into two rows
  const topRowDiscussions = discussions.filter((_, index) => index % 2 === 0);
  const bottomRowDiscussions = discussions.filter((_, index) => index % 2 === 1);

  // Auto-scroll effect for top row (left to right)
  useEffect(() => {
    if (!topRowRef.current || topRowDiscussions.length === 0 || isPaused) return;

    const container = topRowRef.current;
    const scrollSpeed = 1;
    const scrollInterval = 30;

    const autoScroll = setInterval(() => {
      if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
        container.scrollLeft = 0;
      } else {
        container.scrollLeft += scrollSpeed;
      }
    }, scrollInterval);

    return () => clearInterval(autoScroll);
  }, [topRowDiscussions, isPaused]);

  // Auto-scroll effect for bottom row (right to left)
  useEffect(() => {
    if (!bottomRowRef.current || bottomRowDiscussions.length === 0 || isPaused) return;

    const container = bottomRowRef.current;
    const scrollSpeed = 1;
    const scrollInterval = 30;

    // Start from the end for reverse scrolling
    if (container.scrollLeft === 0) {
      container.scrollLeft = container.scrollWidth - container.clientWidth;
    }

    const autoScroll = setInterval(() => {
      if (container.scrollLeft <= 0) {
        container.scrollLeft = container.scrollWidth - container.clientWidth;
      } else {
        container.scrollLeft -= scrollSpeed;
      }
    }, scrollInterval);

    return () => clearInterval(autoScroll);
  }, [bottomRowDiscussions, isPaused]);

  return (
    <section className="py-16 md:py-24 relative">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-12 bg-purple-500/30 rounded-full"></div>
            <MessageSquare className="text-purple-400" size={24} />
            <div className="h-0.5 w-12 bg-purple-500/30 rounded-full"></div>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-white mb-4 font-serif">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Community</span> Discussions
          </h2>
          <p className="text-purple-200/80 text-base md:text-lg max-w-2xl mx-auto">
            Join the conversation! Share your thoughts, ask questions, and connect with fellow seekers.
          </p>
        </div>

        {/* Discussions Carousel - Two Rows */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
          </div>
        ) : discussions.length > 0 ? (
          <div className="relative -mx-4 space-y-4">
            {/* Top Row - Scrolls Left to Right */}
            <div
              ref={topRowRef}
              className="overflow-x-auto scrollbar-hide px-4"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="flex gap-4 pb-2">
                {topRowDiscussions.map((discussion) => (
                  <DiscussionCard key={discussion.id} discussion={discussion} />
                ))}
                {/* Duplicate for seamless looping */}
                {topRowDiscussions.map((discussion) => (
                  <DiscussionCard key={`dup-top-${discussion.id}`} discussion={discussion} />
                ))}
              </div>
            </div>

            {/* Bottom Row - Scrolls Right to Left */}
            <div
              ref={bottomRowRef}
              className="overflow-x-auto scrollbar-hide px-4"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="flex gap-4 pb-2">
                {bottomRowDiscussions.map((discussion) => (
                  <DiscussionCard key={discussion.id} discussion={discussion} />
                ))}
                {/* Duplicate for seamless looping */}
                {bottomRowDiscussions.map((discussion) => (
                  <DiscussionCard key={`dup-bottom-${discussion.id}`} discussion={discussion} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <MessageSquare className="mx-auto text-purple-400/40 mb-4" size={48} />
            <p className="text-purple-200/60">No discussions yet. Be the first to start a conversation!</p>
          </div>
        )}
      </div>

      {/* Add scrollbar hiding CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default DiscussionsSection;
